"use client";

import * as React from "react";
import {
  Text,
  SectionCard,
  CardTitle,
  CardDescription,
  Checkbox,
  CheckboxGroup,
  Badge,
  Section,
} from "@/src/components";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsCheckbox() {
  const [checked, setChecked] = React.useState(false);

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Checkbox
            </Text>
            <Badge variant="secondary">Form</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A checkbox component with label, description, and error state support.
            Built on Radix UI for full accessibility.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/checkbox" />
        </SectionCard>
      </Section>

      {/* Basic */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Basic Usage</CardTitle>
            <CardDescription>Simple checkbox without label</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="flex items-center gap-4">
          <Checkbox checked={checked} onCheckedChange={(c) => setChecked(!!c)} />
          <Text className="text-sm text-muted-foreground">
            Checked: {checked ? "Yes" : "No"}
          </Text>
        </div>
      </SectionCard>
      </Section>

      {/* With Label */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>With Label</CardTitle>
            <CardDescription>Checkbox with integrated label</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <Checkbox label="Accept terms and conditions" />
        <Checkbox label="Subscribe to newsletter" defaultChecked />
        <Checkbox label="Disabled option" disabled />
        <Checkbox label="Disabled checked" disabled defaultChecked />
      </SectionCard>
      </Section>

      {/* With Description */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>With Description</CardTitle>
            <CardDescription>Additional context below the label</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <Checkbox
          label="Marketing emails"
          description="Receive emails about new products, features, and updates"
        />
        <Checkbox
          label="Security alerts"
          description="Get notified about security updates and suspicious activity"
          defaultChecked
        />
      </SectionCard>
      </Section>

      {/* Error State */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Error State</CardTitle>
            <CardDescription>Validation error display</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <Checkbox
          label="I agree to the privacy policy"
          description="You must accept to continue"
          error="This field is required"
        />
      </SectionCard>
      </Section>

      {/* Indeterminate */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Indeterminate State</CardTitle>
            <CardDescription>Partial selection indicator</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <Checkbox
          label="Select all items"
          description="Some items are selected"
          indeterminate
        />
      </SectionCard>
      </Section>

      {/* Checkbox Group */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Checkbox Group</CardTitle>
            <CardDescription>Group related checkboxes with a legend</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-8"
      >
        <CheckboxGroup
          legend="Notification Preferences"
          description="Choose how you want to be notified"
        >
          <Checkbox label="Email notifications" defaultChecked />
          <Checkbox label="SMS notifications" />
          <Checkbox label="Push notifications" />
          <Checkbox label="In-app notifications" defaultChecked />
        </CheckboxGroup>

        <CheckboxGroup
          legend="Select features"
          orientation="horizontal"
        >
          <Checkbox label="Feature A" />
          <Checkbox label="Feature B" />
          <Checkbox label="Feature C" />
        </CheckboxGroup>

        <CheckboxGroup
          legend="Terms"
          error="Please accept all required terms"
        >
          <Checkbox label="Accept terms of service" />
          <Checkbox label="Accept privacy policy" />
        </CheckboxGroup>
      </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
        header={<CardTitle>Props</CardTitle>}
        contentClassName="p-6"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium">Prop</th>
                <th className="text-left py-2 pr-4 font-medium">Type</th>
                <th className="text-left py-2 pr-4 font-medium">Default</th>
                <th className="text-left py-2 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">label</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Label text</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">description</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Help text below label</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">error</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Error message</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">indeterminate</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 pr-4 text-muted-foreground">false</td>
                <td className="py-2 text-muted-foreground">Show indeterminate state</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">checked</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Controlled checked state</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-xs">onCheckedChange</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">(checked: boolean) =&gt; void</td>
                <td className="py-2 pr-4 text-muted-foreground">-</td>
                <td className="py-2 text-muted-foreground">Change callback</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
      </Section>
    </>
  );
}
